// Resolve a refreshable Cloud Storage path for AD audio and mint a new signed URL.
// TTS used to throw away the object path; recordings already kept a uuid. This
// helper covers both, plus legacy signed / Firebase download URLs.

import { auth, functions } from "@app/configs/firebase.js";
import { httpsCallable } from "firebase/functions";
import { environment } from "@app/store/envStore.svelte.js";

const STORAGE_OBJECT_RE = /(?:^|\/)((?:users|teams)\/[^/?#]+\/(?:ad|recordings|storage)\/[^/?#]+)/i;

/**
 * Pull `users|teams/{id}/ad|recordings|storage/{file}` out of a signed or
 * Firebase download URL. Parent-hosted (adLicense) URLs are not GCS objects.
 *
 * @param {string} url
 * @returns {string}
 */
export function parseStoragePathFromUrl(url) {
    if (!url || typeof url !== "string") return "";
    let candidate = url;
    try {
        const parsed = new URL(url);
        if (parsed.pathname.includes("/o/")) {
            candidate = decodeURIComponent(parsed.pathname.split("/o/")[1] || "");
        } else {
            candidate = decodeURIComponent(parsed.pathname);
        }
    } catch {
        try {
            candidate = decodeURIComponent(url);
        } catch {
            candidate = url;
        }
    }
    const match = candidate.match(STORAGE_OBJECT_RE);
    return match ? match[1] : "";
}

/**
 * Canonical Storage path for an event's rendered/recorded audio.
 *
 * @param {object} event
 * @returns {string}
 */
export function getEventAudioStoragePath(event) {
    if (!event || environment.adLicense) return "";
    if (event.audioFileRemote) return event.audioFileRemote;
    const fromUrl = parseStoragePathFromUrl(event.audioFile);
    if (fromUrl) return fromUrl;
    if (event.audioFileUuid && auth.currentUser?.uid) {
        return `users/${auth.currentUser.uid}/recordings/${event.audioFileUuid}.mp3`;
    }
    return "";
}

function parseGoogDate(value) {
    const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/.exec(value);
    if (!match) return NaN;
    return Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +match[6]);
}

/**
 * True when a GCS V2/V4 signed URL is already past its expiry (with skew).
 * Unknown URL formats return false so callers try playback first.
 *
 * @param {string} url
 * @param {number} [skewMs]
 * @returns {boolean}
 */
export function isSignedUrlExpired(url, skewMs = 60_000) {
    if (!url || typeof url !== "string") return false;
    try {
        const parsed = new URL(url);
        const expires = parsed.searchParams.get("Expires") || parsed.searchParams.get("expires");
        if (expires) {
            const exp = Number(expires);
            if (!Number.isFinite(exp)) return false;
            const expMs = exp > 1e12 ? exp : exp * 1000;
            return Date.now() + skewMs >= expMs;
        }
        const googDate = parsed.searchParams.get("X-Goog-Date");
        const googExpires = parsed.searchParams.get("X-Goog-Expires");
        if (googDate && googExpires) {
            const start = parseGoogDate(googDate);
            const ttlSec = Number(googExpires);
            if (!Number.isFinite(start) || !Number.isFinite(ttlSec)) return false;
            return Date.now() + skewMs >= start + ttlSec * 1000;
        }
    } catch {
        return false;
    }
    return false;
}

/**
 * Replace `event.audioFile` with a fresh signed URL. Does not restamp hash,
 * duration, uuid, or peaks — a dead link is a URL problem, not a re-render.
 *
 * @param {object} event
 * @returns {Promise<boolean>}
 */
export async function refreshEventAudioUrl(event) {
    if (!event || environment.adLicense) return false;
    const storagePath = getEventAudioStoragePath(event);
    if (!storagePath) return false;
    try {
        const result = await httpsCallable(functions, "v8GetDownloadLink")(storagePath);
        const url = Array.isArray(result?.data) ? result.data[0] : result?.data;
        if (!url || typeof url !== "string") return false;
        event.audioFile = url;
        event.audioFileRemote = storagePath;
        return true;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[refreshEventAudioUrl]", err?.message);
        return false;
    }
}
